import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getGmailApiUrl, isProductionEnvironment } from '../utils/apiUrls';

const GMAIL_URL = getGmailApiUrl();
const REPROCESS_POLL_INTERVAL_MS = 10000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const COLUMN_ORDER = [
  'timestamp',
  'subject',
  'from_email',
  'to_email',
  'contract_id',
  'listing_id',
  'message_id',
  'from',
  'to',
  'body',
  'cc',
  'bcc',
  'has_attachments',
  'email_filename',
  'attachments',
  'source',
  'created_at',
  'processed_at',
  'stored_at',
  'test_email',
  'db_id'
];

function DeveloperEmails() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20); // 10 | 20 | 50
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);

  // Reprocess state
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [emailToReprocess, setEmailToReprocess] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessProgress, setReprocessProgress] = useState('');
  const [reprocessResult, setReprocessResult] = useState(null);
  const [suppressEmails, setSuppressEmails] = useState(true); // default: suppress outbound emails during reprocess
  const [forceReprocess, setForceReprocess] = useState(true); // skip duplicate-file check — default true for manual reprocessing
  const [skipTmsSync, setSkipTmsSync] = useState(false); // default: do not skip TMS sync

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const isProd = isProductionEnvironment();

  // Inbox diff state
  const [inboxDiff, setInboxDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffError, setDiffError] = useState('');

  // Filters
  const [fromEmail, setFromEmail] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [contractId, setContractId] = useState('');
  const [listingId, setListingId] = useState('');
  const [dateStart, setDateStart] = useState(''); // ISO date (yyyy-mm-dd)
  const [dateEnd, setDateEnd] = useState('');

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      // Redirect non-KeyFlo users back to home
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const buildParams = (resetOffset = true) => {
    const params = {
      user_email: user?.email || '',
      limit: pageSize,
      offset: resetOffset ? 0 : offset
    };

    if (fromEmail) params.from_email = fromEmail;
    if (toEmail) params.to_email = toEmail;
    if (contractId) params.contract_id = contractId;
    if (listingId) params.listing_id = listingId;
    if (dateStart) params.date_from = new Date(dateStart).toISOString();
    if (dateEnd) {
      const end = new Date(dateEnd);
      end.setHours(23, 59, 59, 999);
      params.date_to = end.toISOString();
    }
    return params;
  };

  const fetchEmails = async (reset = true, overrideParams = null) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');

      const params = overrideParams || buildParams(reset);
      const response = await axios.get(`${GMAIL_URL}/emails`, { params });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch emails');
      }

      const newEmails = response.data.emails || [];
      setEmails(reset ? newEmails : [...emails, ...newEmails]);
      setOffset((reset ? 0 : offset) + newEmails.length);
    } catch (e) {
      setError(e.message || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchEmails(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  const fetchInboxDiff = async () => {
    try {
      setLoadingDiff(true);
      setDiffError('');
      const response = await axios.get(`${GMAIL_URL}/admin/inbox-diff`, {
        params: { user_email: user?.email || '', max_orphaned: 200 }
      });
      setInboxDiff(response.data);
    } catch (e) {
      setDiffError(e.response?.data?.error || e.message || 'Failed to load inbox diff');
    } finally {
      setLoadingDiff(false);
    }
  };

  const handleReprocessClick = (email) => {
    setEmailToReprocess(email);
    setReprocessResult(null);
    setReprocessProgress('');
    setShowReprocessModal(true);
  };

  const handleDeleteClick = (email) => {
    setEmailToDelete(email);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!emailToDelete) return;
    const docId = emailToDelete.db_id;
    if (!docId) {
      setDeleteError('No Firestore document ID (db_id) found for this email.');
      return;
    }
    try {
      setDeleting(true);
      setDeleteError('');
      await axios.delete(`${GMAIL_URL}/emails/delete-one/${encodeURIComponent(docId)}`, {
        params: { user_email: user?.email || '' }
      });
      setEmails(prev => prev.filter(e => e.db_id !== docId));
      setShowDeleteModal(false);
      setEmailToDelete(null);
    } catch (e) {
      setDeleteError(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const pollReprocessJob = async (jobId) => {
    while (true) {
      const response = await axios.get(
        `${GMAIL_URL}/admin/reprocess-message/${encodeURIComponent(jobId)}`,
        { params: { user_email: user?.email || '' } },
      );
      const { status } = response.data;
      if (status === 'pending') {
        setReprocessProgress('Queued — waiting to start…');
      } else if (status === 'running') {
        setReprocessProgress('Processing email (classification, splits, compliance)…');
      } else if (status === 'completed') {
        return { success: true, data: response.data.result || response.data };
      } else if (status === 'failed') {
        return { success: false, error: response.data.error || 'Reprocess failed' };
      } else {
        return {
          success: false,
          error: response.data.error || `Unexpected reprocess job status: ${status || 'unknown'}`,
        };
      }
      await sleep(REPROCESS_POLL_INTERVAL_MS);
    }
  };

  const confirmReprocess = async () => {
    if (!emailToReprocess) return;
    try {
      setReprocessing(true);
      setReprocessProgress('Starting reprocess job…');
      const response = await axios.post(`${GMAIL_URL}/admin/reprocess-message`, {
        gmail_api_message_id: emailToReprocess.gmail_api_message_id || '',
        rfc822_message_id: emailToReprocess.message_id || '',
        subject: emailToReprocess.subject || '',
        from_email: emailToReprocess.from_email || '',
        user_email: user?.email || '',
        suppress_emails: suppressEmails,
        force_reprocess: forceReprocess,
        skip_tms_sync: skipTmsSync,
      });

      if (response.status === 202 && response.data?.job_id) {
        setReprocessProgress('Job accepted — polling for completion…');
        const outcome = await pollReprocessJob(response.data.job_id);
        setReprocessResult(outcome);
        return;
      }

      if (response.data?.status === 'success') {
        setReprocessResult({ success: true, data: response.data });
        return;
      }

      setReprocessResult({
        success: false,
        error: response.data?.error || 'Unexpected reprocess response',
      });
    } catch (e) {
      setReprocessResult({
        success: false,
        error: e.response?.data?.error || e.message || 'Reprocess failed',
      });
    } finally {
      setReprocessing(false);
      setReprocessProgress('');
    }
  };

  if (!isAuthenticated) {
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
      <p>Please log in</p>
      <button onClick={() => loginWithRedirect({
        authorizationParams: {
          prompt: 'login'
        }
      })}>Log in</button>
    </div>
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Emails (Firestore)</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>From Email</label>
          <input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="from_email" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>To Email</label>
          <input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="to_email" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Listing ID</label>
          <input value={listingId} onChange={(e) => setListingId(e.target.value)} placeholder="listing_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchEmails(true); }}>Apply Filters</button>
        <button onClick={() => {
          setFromEmail(''); setToEmail(''); setContractId(''); setListingId('');
          setDateStart(''); setDateEnd(''); setOffset(0);
          fetchEmails(true, { user_email: user?.email || '', limit: pageSize, offset: 0 });
        }}>Clear</button>
        <button onClick={() => fetchEmails(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, left: 0, zIndex: 2, minWidth: 100 }}>
                Actions
              </th>
              {COLUMN_ORDER.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {emails.map((email, idx) => (
              <tr key={email.db_id || email.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', position: 'sticky', left: 0, zIndex: 1, background: '#ffffff', minWidth: 130 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <button
                      onClick={() => handleReprocessClick(email)}
                      style={{
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Reprocess
                    </button>
                    <button
                      onClick={() => handleDeleteClick(email)}
                      style={{
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
                {COLUMN_ORDER.map((col) => {
                  let val = email[col];
                  if (col === 'timestamp' || col === 'created_at' || col === 'processed_at' || col === 'stored_at') {
                    val = val ? formatDateTime(val) : '';
                  }
                  if (col === 'attachments' && Array.isArray(val)) {
                    val = val.map((a) => (typeof a === 'string' ? a : a?.filename || a?.name || '')).filter(Boolean).join(', ');
                  }
                  if (col === 'has_attachments' && typeof val === 'boolean') {
                    val = val ? 'true' : 'false';
                  }
                  if (val === null || typeof val === 'undefined') val = '';
                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minWidth: col === 'body' ? 500 : 280,
                        maxWidth: col === 'body' ? 1500 : undefined
                      }}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {emails.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMN_ORDER.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No emails found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Inbox Diff — Gmail messages not in Firestore */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Inbox Messages Not in KeyFlo</h3>
          <button onClick={fetchInboxDiff} disabled={loadingDiff} style={{ padding: '6px 14px', cursor: 'pointer' }}>
            {loadingDiff ? 'Scanning...' : 'Scan Inbox'}
          </button>
          {inboxDiff && (
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              {inboxDiff.gmail_inbox_count} in Gmail · {inboxDiff.firestore_count} in Firestore · {inboxDiff.orphaned_count} orphaned
              {inboxDiff.capped_at && ` (capped at ${inboxDiff.capped_at})`}
            </span>
          )}
        </div>
        {diffError && <div style={{ color: 'red', marginBottom: '8px' }}>{diffError}</div>}
        {inboxDiff && inboxDiff.orphaned.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>No orphaned messages — all inbox emails are in Firestore.</div>
        )}
        {inboxDiff && inboxDiff.orphaned.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 110 }}>Actions</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>From</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Subject</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Snippet</th>
                </tr>
              </thead>
              <tbody>
                {inboxDiff.orphaned.map((msg) => (
                  <tr key={msg.gmail_api_message_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                      <button
                        onClick={() => handleReprocessClick({
                          gmail_api_message_id: msg.gmail_api_message_id,
                          message_id: '',
                          from_email: msg.from_email,
                          subject: msg.subject,
                        })}
                        style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        Reprocess
                      </button>
                    </td>
                    <td style={{ padding: '8px 12px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{msg.from_email}</td>
                    <td style={{ padding: '8px 12px', verticalAlign: 'top' }}>{msg.subject}</td>
                    <td style={{ padding: '8px 12px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{formatDateTime(msg.date)}</td>
                    <td style={{ padding: '8px 12px', verticalAlign: 'top', color: '#6b7280', fontSize: '0.85rem', maxWidth: '400px' }}>{msg.snippet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '420px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#ef4444' }}>Delete Email Record</h3>
            {isProd && (
              <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center', border: '2px solid #dc2626', borderRadius: '4px', padding: '8px' }}>
                THIS IS THE PROD ENV. PLEASE BE 100% SURE YOU WANT TO DELETE.
              </p>
            )}
            <p>
              Permanently delete the Firestore record for the email from <strong>{emailToDelete?.from_email}</strong>
              {emailToDelete?.subject ? ` — "${emailToDelete.subject}"` : ''}?
            </p>
            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              This removes the email from KeyFlo. It will reappear in the Inbox Diff and can be reprocessed again.
            </p>
            {deleteError && <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{deleteError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setEmailToDelete(null); setDeleteError(''); }}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Delete Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReprocessModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '460px', maxWidth: '90%' }}>
            {!reprocessResult ? (
              <>
                <h3 style={{ marginTop: 0, color: '#f59e0b' }}>Reprocess Email</h3>
                <p>
                  Reprocess the original email from <strong>{emailToReprocess?.from_email}</strong>
                  {emailToReprocess?.subject ? ` — "${emailToReprocess.subject}"` : ''}?
                </p>
                <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  This will clear the duplicate-check record and re-run the email through the full processing pipeline using the original sender.
                </p>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!suppressEmails}
                    onChange={e => setSuppressEmails(!e.target.checked)}
                  />
                  Send emails normally (auto-responses, onboarding emails)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#374151', cursor: 'pointer', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={forceReprocess}
                    onChange={e => setForceReprocess(e.target.checked)}
                  />
                  Force reprocess (skip duplicate file check)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#374151', cursor: 'pointer', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={skipTmsSync}
                    onChange={e => setSkipTmsSync(e.target.checked)}
                  />
                  Skip TMS sync (do not upload documents to SkySlope/TMS)
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                  <button
                    onClick={() => { setShowReprocessModal(false); setEmailToReprocess(null); }}
                    disabled={reprocessing}
                    style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: reprocessing ? 'not-allowed' : 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmReprocess}
                    disabled={reprocessing}
                    style={{ background: '#f59e0b', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px', cursor: reprocessing ? 'not-allowed' : 'pointer' }}
                  >
                    {reprocessing ? 'Reprocessing…' : 'Reprocess Email'}
                  </button>
                </div>
                {reprocessing && reprocessProgress ? (
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
                    {reprocessProgress} (checking every 10s — large bundles may take several minutes)
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0, color: reprocessResult.success ? '#10b981' : '#ef4444' }}>
                  {reprocessResult.success ? 'Reprocess Complete' : 'Reprocess Failed'}
                </h3>
                {reprocessResult.success ? (
                  <p>The email has been reprocessed. Check Slack alerts or refresh the contracts/listings pages to see the updated results.</p>
                ) : (
                  <p style={{ color: '#ef4444' }}>{reprocessResult.error}</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button
                    onClick={() => { setShowReprocessModal(false); setEmailToReprocess(null); setReprocessResult(null); setReprocessProgress(''); }}
                    style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperEmails;


